import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, Car, ChevronRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { fmtGemeldOp } from '@/lib/datumTijd';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// R7.3: huidige polis-/klantnummer van de locatie (pagina is manager+)
const VerzekeringCellen = ({ row }) => {
  const { isAdmin } = useAuth();
  const v = row.verzekering || {};
  if (!v.polisnummer && !v.klantnummer) {
    return (
      <td colSpan={3} className="py-3 px-4 text-xs text-amber-700" data-testid={`row-verzekering-leeg-${row.locatie_naam}`}>
        Polis-/klantnummer nog niet ingevuld bij deze locatie{' '}
        {isAdmin() && <Link to="/locatie-info" onClick={(e) => e.stopPropagation()} className="underline font-medium">Locatie-info</Link>}
      </td>
    );
  }
  return (
    <>
      <td className="py-3 px-4 text-slate-700 whitespace-nowrap" data-testid={`row-verzekeraar-${row.locatie_naam}`}>{v.verzekeraar || '-'}</td>
      <td className="py-3 px-4 font-mono text-slate-700 whitespace-nowrap" data-testid={`row-klantnummer-${row.locatie_naam}`}>{v.klantnummer || '-'}</td>
      <td className="py-3 px-4 font-mono text-slate-700 whitespace-nowrap" data-testid={`row-polisnummer-${row.locatie_naam}`}>{v.polisnummer || '-'}</td>
    </>
  );
};

const SchademeldingenOverzicht = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/schademeldingen/management/overzicht`, { headers: hdrs() });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error('Laden mislukt');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totaalNieuw = data.reduce((s, r) => s + (r.nieuw || 0), 0);
  const totaalAll = data.reduce((s, r) => s + (r.totaal || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 sm:px-0" data-testid="schademeldingen-overzicht-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="schademeldingen-overzicht-title">
          <Car className="w-6 h-6" /> Schademeldingen Overzicht
        </h1>
        <p className="text-slate-500">Overzicht van alle locaties ({totaalAll} meldingen totaal)</p>
      </div>

      {/* Warning banner */}
      {totaalNieuw > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-800" data-testid="nieuw-warning">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{totaalNieuw} nieuwe schademelding{totaalNieuw !== 1 ? 'en' : ''} wachten op afhandeling</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto" data-testid="overzicht-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-4 font-semibold text-slate-700">Locatie</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Totaal</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Nieuw</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">In behandeling</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Afgehandeld</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Laatst gemeld op</th>
                <th className="py-3 px-4 font-semibold text-slate-700" colSpan={3}>Verzekeraar / Klantnummer / Polisnummer</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.map((row) => (
                <tr
                  key={row.locatie_naam}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/schademeldingen?locatie_id=${row.locatie_id}`)}
                  data-testid={`row-${row.locatie_naam}`}
                >
                  <td className="py-3 px-4 font-medium text-slate-700">{row.locatie_naam}</td>
                  <td className="py-3 px-4 text-center text-slate-600">{row.totaal}</td>
                  <td className="py-3 px-4 text-center">
                    {row.nieuw > 0 ? (
                      <Badge className="bg-red-100 text-red-700 text-xs">{row.nieuw}</Badge>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {row.in_behandeling > 0 ? (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">{row.in_behandeling}</Badge>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {row.afgehandeld > 0 ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">{row.afgehandeld}</Badge>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-600 whitespace-nowrap" data-testid={`row-gemeld-op-${row.locatie_naam}`}>{fmtGemeldOp(row.laatste_gemeld_op, row.laatste_melding)}</td>
                  <VerzekeringCellen row={row} />
                  <td className="py-3 px-4"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-slate-400" data-testid="overzicht-empty">
                    Geen schademeldingen gevonden
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SchademeldingenOverzicht;
